import { apiClient } from '@/lib/api-client';
import type {
  FoodTag,
  FoodTagInput,
  FoodItem,
  PaginatedResponse,
  TagCategory,
} from '@/types/nutrition';

/**
 * The actual shape returned by the get_foods_by_tags backend action.
 * It does not follow standard DRF pagination — foods live under the "foods"
 * key and next-page availability is signalled by "has_next" (not "next").
 */
export interface FoodsByTagsResponse {
  include_tags: string[];
  exclude_tags: string[];
  match_all: boolean;
  count: number;
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
  foods: FoodItem[];
}

const BASE_URL = 'nutrition/food-tags';

export const tagsApi = {
  list: async (params?: {
    category?: TagCategory;
    is_active?: boolean;
    search?: string;
  }): Promise<FoodTag[]> => {
    const { data } = await apiClient.get<FoodTag[] | PaginatedResponse<FoodTag>>(BASE_URL, {
      params,
    });

    // Handle both array and paginated responses
    if (Array.isArray(data)) {
      return data;
    }
    // If paginated, return results array
    return data.results || [];
  },

  get: async (id: number): Promise<FoodTag> => {
    const { data } = await apiClient.get<FoodTag>(`${BASE_URL}/${id}`);
    return data;
  },

  create: async (input: FoodTagInput): Promise<FoodTag> => {
    const { data } = await apiClient.post<FoodTag>(BASE_URL, input);
    return data;
  },

  bulkCreate: async (
    items: FoodTagInput[]
  ): Promise<{
    created_count: number;
    created_ids: number[];
    updated_count: number;
    updated_ids: number[];
    errors: Array<{ index: number; error: string }>;
  }> => {
    const { data } = await apiClient.post<{
      created_count: number;
      created_ids: number[];
      updated_count: number;
      updated_ids: number[];
      errors: Array<{ index: number; error: string }>;
    }>(`${BASE_URL}/bulk`, items);
    return data;
  },

  update: async (id: number, input: Partial<FoodTagInput>): Promise<FoodTag> => {
    const { data } = await apiClient.patch<FoodTag>(`${BASE_URL}/${id}`, input);
    return data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  getByCategory: async (): Promise<Record<TagCategory, FoodTag[]>> => {
    const { data } = await apiClient.get<Record<TagCategory, FoodTag[]>>(`${BASE_URL}/by_category`);
    return data;
  },

  getFoodsWithTag: async (id: number): Promise<PaginatedResponse<FoodItem>> => {
    const { data } = await apiClient.get<PaginatedResponse<FoodItem>>(`${BASE_URL}/${id}/foods`);
    return data;
  },

  getFoodsByTags: async (params: {
    include_tags: number[];
    exclude_tags?: string[];
    match_all?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<FoodsByTagsResponse | PaginatedResponse<FoodItem> | FoodItem[]> => {
    const { data } = await apiClient.post<
      FoodsByTagsResponse | PaginatedResponse<FoodItem> | FoodItem[]
    >(`${BASE_URL}/get_foods_by_tags`, params);
    return data;
  },

  getTagsByFoods: async (params: {
    food_ids: number[];
    group_by_category?: boolean;
  }): Promise<FoodTag[]> => {
    const { data } = await apiClient.post<FoodTag[]>(`${BASE_URL}/get_tags_by_foods`, params);
    return data;
  },

  getCommonTags: async (foodIds: number[]): Promise<FoodTag[]> => {
    const { data } = await apiClient.post<FoodTag[]>(`${BASE_URL}/get_common_tags`, {
      food_ids: foodIds,
    });
    return data;
  },

  bulkTagFoods: async (tagSlug: string, foodIds: number[]): Promise<void> => {
    await apiClient.post(`${BASE_URL}/bulk_tag_foods`, {
      tag_slug: tagSlug,
      food_ids: foodIds,
    });
  },

  bulkUntagFoods: async (tagSlug: string, foodIds: number[]): Promise<void> => {
    await apiClient.post(`${BASE_URL}/bulk_untag_foods`, {
      tag_slug: tagSlug,
      food_ids: foodIds,
    });
  },

  mergeInto: async (id: number, targetTagId: number): Promise<FoodTag> => {
    const { data } = await apiClient.post<FoodTag>(`${BASE_URL}/${id}/merge_into`, {
      target_tag_id: targetTagId,
    });
    return data;
  },

  getForRecommendations: async (): Promise<FoodTag[]> => {
    const { data } = await apiClient.get<FoodTag[]>(`${BASE_URL}/for_recommendations`);
    return data;
  },

  getForAvoidance: async (): Promise<FoodTag[]> => {
    const { data } = await apiClient.get<FoodTag[]>(`${BASE_URL}/for_avoidance`);
    return data;
  },
};
